import React, { useState, useEffect, useMemo } from "react";
import api from "./services/api";
import "react-toastify/dist/ReactToastify.css";
import { QueryClient, QueryClientProvider } from "react-query";
import { ptBR } from "@material-ui/core/locale";
import { createTheme, ThemeProvider, StylesProvider } from "@material-ui/core/styles";
import CssBaseline from "@material-ui/core/CssBaseline";
import { useMediaQuery } from "@material-ui/core";
import ColorModeContext from "./layout/themeContext";
import { ActiveMenuProvider } from "./context/ActiveMenuContext";
import Favicon from "react-favicon";
import { getBackendUrl } from "./config";
import Routes from "./routes";
import defaultLogoLight from "./assets/logo.png";
import defaultLogoDark from "./assets/logo-black.png";
import defaultLogoFavicon from "./assets/favicon.ico";
import useSettings from "./hooks/useSettings";
import { i18n } from "./translate/i18n";

import "./styles/animations.css";
import "./styles/react-flow.css";

import moment from "moment";
import "moment/locale/pt-br";
import "moment/locale/es";
import "moment/locale/en-gb";
import "moment/locale/fr";
import "moment/locale/ar";

const getMomentLocale = (lng) => {
  if (lng === "pt") return "pt-br";
  if (lng === "en") return "en-gb";
  if (lng === "fr") return "fr";
  if (lng === "ar") return "ar";
  return "es";
};

// Forzar el idioma inicial a moment
moment.locale(getMomentLocale(i18n.language));

// Suscribirse a cambios de idioma
i18n.on("languageChanged", (lng) => {
  moment.locale(getMomentLocale(lng));
});

const queryClient = new QueryClient();

const App = () => {
  const [locale, setLocale] = useState();
  const appColorLocalStorage =
    localStorage.getItem("primaryColorLight") ||
    localStorage.getItem("primaryColorDark") ||
    "#10B981";
  const appNameLocalStorage = localStorage.getItem("appName") || "";
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const preferredTheme = window.localStorage.getItem("preferredTheme");
  const [mode, setMode] = useState(preferredTheme === "dark" ? "dark" : "light");
  const [primaryColorLight, setPrimaryColorLight] =
    useState(appColorLocalStorage);
  const [primaryColorDark, setPrimaryColorDark] =
    useState(appColorLocalStorage);
  const [appLogoLight, setAppLogoLight] = useState(defaultLogoLight);
  const [appLogoDark, setAppLogoDark] = useState(defaultLogoDark);
  const [appLogoFavicon, setAppLogoFavicon] = useState(defaultLogoFavicon);
  const [appName, setAppName] = useState(appNameLocalStorage);
  const { getPublicSetting } = useSettings();

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === "light" ? "dark" : "light";
          window.localStorage.setItem("preferredTheme", newMode); // Persistindo o tema no localStorage
          return newMode;
        });
      },
      setMode: (newMode) => {
        setMode(newMode);
        window.localStorage.setItem("preferredTheme", newMode);
      },
      setPrimaryColorLight,
      setPrimaryColorDark,
      setAppLogoLight,
      setAppLogoDark,
      setAppLogoFavicon,
      setAppName,
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      mode,
    }),
    [appLogoLight, appLogoDark, appLogoFavicon, appName, mode]
  );

  const theme = useMemo(
    () =>
      createTheme(
        {
          // Scrollbar styles melhorados mas usando cores do tema
          scrollbarStyles: {
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
              margin: "8px 0",
            },
            "&::-webkit-scrollbar-thumb": {
              background: mode === "light" ? `${primaryColorLight}80` : `${primaryColorDark}66`,
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: mode === "light" ? primaryColorLight : primaryColorDark,
            },
          },

          scrollbarStylesSoft: {
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
              margin: "8px 0",
            },
            "&::-webkit-scrollbar-thumb": {
              background: mode === "light" ? `${primaryColorLight}80` : `${primaryColorDark}66`,
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: mode === "light" ? primaryColorLight : primaryColorDark,
            },
          },

          scrollbarStylesNeutral: {
            "&::-webkit-scrollbar": {
              width: "6px",
              height: "6px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
              margin: "6px 0",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: mode === "light" 
                ? "rgba(0, 0, 0, 0.22)" 
                : "rgba(255, 255, 255, 0.25)",
              borderRadius: "10px",
              minHeight: "40px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: mode === "light" 
                ? "rgba(0, 0, 0, 0.38)" 
                : "rgba(255, 255, 255, 0.45)",
            },
            "&::-webkit-scrollbar-corner": {
              backgroundColor: "transparent",
            },
          },

          palette: {
            type: mode,
            primary: {
              main: mode === "light" ? primaryColorLight : primaryColorDark, // Usa cores dinâmicas
              light: mode === "light"
                ? `${primaryColorLight}80`
                : `${primaryColorDark}80`,
              dark: mode === "light"
                ? `${primaryColorLight}CC`
                : `${primaryColorDark}CC`,
              contrastText: "#ffffff",
            },
            textPrimary:
              mode === "light" ? primaryColorLight : primaryColorDark,
            borderPrimary:
              mode === "light" ? primaryColorLight : primaryColorDark,
            dark: { main: mode === "light" ? "#333333" : "#F3F3F3" },
            light: { main: mode === "light" ? "#F3F3F3" : "#333333" },
            fontColor: mode === "light" ? primaryColorLight : primaryColorDark,
            tabHeaderBackground: mode === "light" ? "#EEE" : "#666",
            optionsBackground: mode === "light" ? "#fafafa" : "#333",
            fancyBackground: mode === "light" ? "#fafafa" : "#333",
            total: mode === "light" ? "#fff" : "#222",
            messageIcons: mode === "light" ? "grey" : "#F3F3F3",
            inputBackground: mode === "light" ? "#FFFFFF" : "#333",
            barraSuperior: mode === "light" ? primaryColorLight : "#666", // Usa cor do tema
            background: {
              default: mode === "light" ? "#f5f5f5" : "#1a1c20",
              paper: mode === "light" ? "#ffffff" : "#2A2D32",
            },
          },

          typography: {
            fontFamily: [
              'Inter',
              'Roboto',
              '-apple-system',
              'BlinkMacSystemFont',
              '"Segoe UI"',
              '"Helvetica Neue"',
              'Arial',
              'sans-serif',
            ].join(','),
            h1: {
              fontWeight: 500,
              letterSpacing: '-0.025em',
            },
            h2: {
              fontWeight: 500,
              letterSpacing: '-0.025em',
            },
            h3: {
              fontWeight: 400,
              letterSpacing: '-0.025em',
            },
            h4: {
              fontWeight: 400,
              letterSpacing: '-0.025em',
            },
            h5: {
              fontWeight: 400,
              letterSpacing: '-0.025em',
            },
            h6: {
              fontWeight: 400,
              letterSpacing: '-0.025em',
            },
            body1: {
              fontWeight: 300,
            },
            body2: {
              fontWeight: 300,
            },
            subtitle1: {
              fontWeight: 400,
            },
            subtitle2: {
              fontWeight: 400,
            },
            button: {
              fontWeight: 500,
              textTransform: 'none',
              letterSpacing: '0.025em',
            },
          },

          shape: {
            borderRadius: 8, // Bordas arredondadas mas não excessivas
          },
          overrides: {
            MuiCssBaseline: {
              '@global': {
                '*::-webkit-scrollbar': {
                  width: '8px',
                  height: '8px',
                },
                '*::-webkit-scrollbar-track': {
                  backgroundColor: 'transparent',
                  margin: '8px 0',
                },
                '*::-webkit-scrollbar-thumb': {
                  backgroundColor: mode === 'light' ? `${primaryColorLight}80` : `${primaryColorDark}66`,
                  borderRadius: '10px',
                },
                '*::-webkit-scrollbar-thumb:hover': {
                  backgroundColor: mode === 'light' ? primaryColorLight : primaryColorDark,
                },
                '*::-webkit-scrollbar-corner': {
                  backgroundColor: 'transparent',
                },
              },
            },
            MuiTypography: {
              root: {
                fontWeight: 'inherit',
              },
              colorPrimary: {
                // Fix dark mode text visibility by making primary text white
                color: mode === "light" ? primaryColorLight : "#FFFFFF",
              },
              colorTextPrimary: {
                // For text that inherits primary color
                textShadow: mode === "light" ? "none" : "1px 1px 2px rgba(0,0,0,0.5)",
              }
            },
            // Botões usando cor do tema
            MuiButton: {
              root: {
                borderRadius: 8,
                textTransform: 'none',
                fontWeight: 600,
                letterSpacing: '0.025em',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                }
              },
              contained: {
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                }
              }
            },

            MuiContainer: {
              root: {
                paddingLeft: '0 !important',
                paddingRight: '0 !important',
                maxWidth: 'none !important',
                width: '100% !important',
              },
              maxWidthLg: {
                maxWidth: 'none !important',
              },
              maxWidthMd: {
                maxWidth: 'none !important',
              },
              maxWidthSm: {
                maxWidth: 'none !important',
              },
              maxWidthXl: {
                maxWidth: 'none !important',
              },
              maxWidthXs: {
                maxWidth: 'none !important',
              },
            },

            MuiAccordion: {
              root: {
                borderRadius: '0px !important',
                overflow: 'hidden',
                '&:first-child': {
                  borderTopLeftRadius: '0px !important',
                  borderTopRightRadius: '0px !important',
                },
                '&:last-child': {
                  borderBottomLeftRadius: '0px !important',
                  borderBottomRightRadius: '0px !important',
                },
                '&.Mui-expanded': {
                  margin: '8px 0',
                  borderRadius: '0px !important',
                }
              }
            },
            MuiAccordionSummary: {
              root: {
                borderRadius: '0px !important',
              }
            },
            MuiDialog: {
              paper: {
                borderRadius: '16px !important',
                border: mode === 'dark' 
                  ? `1px solid ${primaryColorDark} !important` 
                  : `1px solid ${primaryColorLight} !important`,
                boxShadow: mode === 'dark' 
                  ? `0 0 20px ${primaryColorDark}80 !important` 
                  : `0 0 16px ${primaryColorLight}40 !important`,
                backgroundColor: mode === 'dark' ? '#1E2126 !important' : '#FFFFFF !important',
                overflow: 'hidden !important',
                display: 'flex !important',
                flexDirection: 'column !important',
              },
            },
            MuiDialogContent: {
              root: {
                overflowY: 'auto !important',
                minHeight: 0,
                flex: '1 1 auto',
                '&::-webkit-scrollbar': {
                  width: '8px',
                  height: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: mode === 'light' ? `${primaryColorLight}80` : `${primaryColorDark}66`,
                  borderRadius: '10px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  backgroundColor: mode === 'light' ? primaryColorLight : primaryColorDark,
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'transparent',
                  margin: '8px 0',
                },
              },
            },
            MuiDialogActions: {
              root: {
                padding: '16px 40px',
                flexShrink: 0,
              }
            },
            MuiPaper: {
              root: {
                backgroundImage: 'none',
                marginLeft: 0,
                marginRight: 0,
                width: '100%',
                backgroundClip: 'padding-box',
              },
              rounded: {
                borderRadius: 12,
                overflow: "hidden",
              },
              elevation1: {
                boxShadow: mode === "light" 
                  ? `0 0 10px ${primaryColorLight}33` 
                  : `0 0 12px ${primaryColorDark}80`,
                border: mode === "dark" ? `1px solid ${primaryColorDark}` : `1px solid ${primaryColorLight}`,
              },
              elevation2: {
                boxShadow: mode === "light"
                  ? `0 2px 8px ${primaryColorLight}26`
                  : '0 2px 6px rgba(0, 0, 0, 0.1)',
              },
              elevation3: {
                boxShadow: mode === "light"
                  ? `0 4px 14px ${primaryColorLight}33`
                  : '0 4px 12px rgba(0, 0, 0, 0.1)',
              },
              outlined: {
                boxShadow: mode === "dark" ? `0 0 12px ${primaryColorDark}80` : `0 0 10px ${primaryColorLight}40`,
                border: mode === "dark" ? `1px solid ${primaryColorDark}` : `1px solid ${primaryColorLight}`,
                borderRadius: 16,
                backgroundColor: mode === "dark" ? "#2A2D32 !important" : "#ffffff",
                transform: "translateZ(0)",
                backfaceVisibility: "hidden",
              }
            },

                        MuiTabs: {
              root: {
                minHeight: 38,
                borderRadius: 10,
              },
              indicator: {
                backgroundColor: mode === "light" ? primaryColorLight : primaryColorDark,
                height: 3,
                borderRadius: "3px 3px 0 0",
              },
            },
            MuiTab: {
              root: {
                textTransform: "none",
                minHeight: 38,
                fontWeight: 600,
                fontSize: "0.85rem",
                borderRadius: 8,
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.04)",
                },
                "&$selected": {
                  color: mode === "light" ? primaryColorLight : primaryColorDark,
                  fontWeight: 700,
                },
              },
            },
            // ⭐ ADICIONAR: Proteção específica para menus
            MuiMenu: {
              paper: {
                width: 'auto !important',
                maxWidth: '300px !important',
                minWidth: '180px !important',
                backgroundColor: mode === "light" ? "#ffffff" : "#1e1e1e",
              }
            },
            
            MuiOutlinedInput: {
              root: {
                borderRadius: 8,
                backgroundColor: mode === "light" ? "#ffffff" : "#1e1e1e",
                "&:hover $notchedOutline": {
                  borderColor: mode === "light" ? `${primaryColorLight}66` : `${primaryColorDark}66`,
                },
                "&$focused $notchedOutline": {
                  borderColor: mode === "light" ? primaryColorLight : primaryColorDark,
                },
              },
              notchedOutline: {
                borderColor: mode === "light" ? "rgba(0, 0, 0, 0.23)" : "rgba(255, 255, 255, 0.15)",
              },
            },
            
            MuiSelect: {
              select: {
                backgroundColor: mode === "light" ? "transparent" : "#1e1e1e",
                borderRadius: 8,
              },
              icon: {
                color: mode === "light" ? "#000000" : "#ffffff",
              }
            },

            // ⭐ ADICIONAR: Proteção específica para popovers
            MuiPopover: {
              paper: {
                width: 'auto !important',
                maxWidth: '300px !important',
                minWidth: 'auto !important',
                backgroundColor: mode === "light" ? "#ffffff" : "#1e1e1e",
              }
            },

            // Forzar fuente Inter en textos tipeados (Inputs) y Dropdowns
            MuiInputBase: {
              root: {
                fontFamily: "'Inter', 'Roboto', sans-serif !important",
                fontWeight: 400,
                color: mode === "light" ? "#000000" : "#ffffff",
              },
            },
            MuiMenuItem: {
              root: {
                fontFamily: "'Inter', 'Roboto', sans-serif !important",
                color: mode === "light" ? "#000000" : "#ffffff",
                "&.Mui-selected": {
                  backgroundColor: mode === "light" ? "rgba(0, 0, 0, 0.08)" : "rgba(255, 255, 255, 0.16) !important",
                },
                "&:hover": {
                  backgroundColor: mode === "light" ? "rgba(0, 0, 0, 0.04)" : "rgba(255, 255, 255, 0.08) !important",
                }
              },
            },



            // Drawer sem bordas
            MuiDrawer: {
              paper: {
                border: 'none',
              }
            },

            // AppBar transparente
            MuiAppBar: {
              root: {
                boxShadow: 'none',
              }
            },

            // Corregir color de fondo del encabezado de tablas para que encaje con el borde neón
            MuiTableCell: {
              head: {
                backgroundColor: mode === "light" ? "#fafafa" : "#424242",
              },
              stickyHeader: {
                backgroundColor: mode === "light" ? "#fafafa" : "#424242",
              }
            },
            MuiTableHead: {
              root: {
                "& th:first-child": {
                  borderTopLeftRadius: "11px !important",
                },
                "& th:last-child": {
                  borderTopRightRadius: "11px !important",
                }
              }
            }
          },

          mode,
          appLogoLight,
          appLogoDark,
          appLogoFavicon,
          appName,
          calculatedLogoDark: () => {
            if (
              appLogoDark === defaultLogoDark &&
              appLogoLight !== defaultLogoLight
            ) {
              return appLogoLight;
            }
            return appLogoDark;
          },
          calculatedLogoLight: () => {
            if (
              appLogoDark !== defaultLogoDark &&
              appLogoLight === defaultLogoLight
            ) {
              return appLogoDark;
            }
            return appLogoLight;
          },
        },
        locale
      ),
    [
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      locale,
      mode,
      primaryColorDark,
      primaryColorLight, // Essas são as cores que vêm do tema dinâmico
    ]
  );

  useEffect(() => {
    window.localStorage.setItem("preferredTheme", mode);
  }, [mode]);

  useEffect(() => {
    const savedLight = localStorage.getItem("primaryColorLight");
    const savedDark = localStorage.getItem("primaryColorDark");
    getPublicSetting("primaryColorLight")
      .then((color) => {
        setPrimaryColorLight(savedLight || color || "#10B981");
      })
      .catch((error) => {
        setPrimaryColorLight(savedLight || "#10B981");
      });
    getPublicSetting("primaryColorDark")
      .then((color) => {
        setPrimaryColorDark(savedDark || color || savedLight || "#10B981");
      })
      .catch((error) => {
        setPrimaryColorDark(savedDark || savedLight || "#10B981");
      });
    getPublicSetting("appLogoLight")
      .then((file) => {
        setAppLogoLight(
          file ? getBackendUrl() + "/public/" + file : defaultLogoLight
        );
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoDark")
      .then((file) => {
        setAppLogoDark(
          file ? getBackendUrl() + "/public/" + file : defaultLogoDark
        );
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoFavicon")
      .then((file) => {
        setAppLogoFavicon(
          file ? getBackendUrl() + "/public/" + file : defaultLogoFavicon
        );
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appName")
      .then((name) => {
        setAppName(name || "BotMix");
      })
      .catch((error) => {
        console.log("Error reading setting", error);
        setAppName("BotMix");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      "--primaryColor",
      mode === "light" ? primaryColorLight : primaryColorDark
    );
  }, [primaryColorLight, primaryColorDark, mode]);

  useEffect(() => {
    async function fetchVersionData() {
      try {
        const response = await api.get("/version");
        const { data } = response;
        window.localStorage.setItem("frontendVersion", data.version);
      } catch (error) {
        console.log("Error fetching data", error);
      }
    }
    fetchVersionData();
  }, []);

  const [currentLang, setCurrentLang] = useState(i18n.language);

  useEffect(() => {
    const handleLanguageChanged = (lang) => {
      setCurrentLang(lang);
    };
    i18n.on("languageChanged", handleLanguageChanged);
    return () => {
      i18n.off("languageChanged", handleLanguageChanged);
    };
  }, []);

  return (
    <>
      <Favicon
        url={
          appLogoFavicon
            ? appLogoFavicon
            : defaultLogoFavicon
        }
      />
      <ColorModeContext.Provider value={{ colorMode }}>
        <StylesProvider injectFirst>
            <ThemeProvider theme={theme}>
          <CssBaseline />
          <QueryClientProvider client={queryClient}>
            <ActiveMenuProvider>
              <Routes key={currentLang} />
            </ActiveMenuProvider>
          </QueryClientProvider>
        </ThemeProvider>
            </StylesProvider>
      </ColorModeContext.Provider>
    </>
  );
};

export default App;
